/**
 * Escolher uma vaga em dois passos: primeiro o DIA, depois o HORÁRIO.
 *
 * ── Por que não a tabela ─────────────────────────────────────────────────
 *
 * Vinte e tantas linhas de "ter, 28/10, 08:30" obrigavam a ler a lista inteira
 * para saber em que dias o médico atende. No calendário isso se vê de relance:
 * o dia com vaga fica em verde (`--ok-fraca` com `--ok`, a cor de "livre"), o
 * dia sem vaga fica apagado e travado; o dia aberto, no roxo da escolha.
 * Desenhado no design system (out/2026).
 *
 * ── O calendário encolhe, e os horários entram ao lado ───────────────────
 *
 * O calendário já abre no primeiro dia com vaga (pedido de 25/10): a recepção
 * vê de cara os horários mais próximos, sem um clique. Ao abrir um dia, a
 * grade troca o peso das duas colunas: o mês encolhe para a esquerda e os
 * horários do dia entram pela direita, no mesmo bloco. O dia
 * escolhido continua à vista enquanto se escolhe a hora. A curva é a
 * `--transicao-entrada` (o que ENTRA na tela), que vale 0ms com movimento
 * reduzido. Em coluna estreita é um passo de cada vez, com o voltar.
 *
 * ── Por mês, quando a agenda é longa (#1187) ──────────────────────────────
 *
 * A clínica agenda até o ano seguinte, com vagas de minuto em minuto: pedir
 * tudo de uma vez seria pesado e viria cortado. Com `paginado`, quem monta o
 * calendário controla o mês e o dia aberto: busca os dias com vaga do mês (só a
 * contagem por dia) e, ao abrir um dia, os horários dele. As setas andam
 * livres entre o primeiro e o último mês que a tela permite.
 *
 * ── Só escolhe ───────────────────────────────────────────────────────────
 *
 * Não confirma nada: quem monta a tela tem o próprio botão ("Marcar
 * consulta"). Os horários são `radio` de verdade, com o teclado do navegador,
 * todos com o mesmo `name`.
 */

import { useId, useState } from 'react';
import { Icone } from '../icones';
// A conta do dia sem fuso é a mesma do `CampoData`; a grade não (ver lá o porquê).
import {
  INICIAIS_DA_SEMANA as INICIAIS,
  MESES,
  SEMANA,
  diaDe,
  hojeLocal,
  partes,
} from '../campo/data-local';
import './calendario-de-vagas.css';

export type VagaDoCalendario = {
  readonly id: string;
  /** `AAAA-MM-DD`, no relógio de quem atende. */
  readonly dia: string;
  /** `HH:MM`. */
  readonly hora: string;
};

/** O calendário por mês (#1187): o mês e o dia aberto são de quem monta. */
export type CalendarioPaginado = {
  /** `AAAA-MM`. */
  readonly mes: string;
  readonly aoMudarMes: (mes: string) => void;
  /** Os dias do mês com vaga, `AAAA-MM-DD` → quantas. */
  readonly diasComVaga: Readonly<Record<string, number>>;
  readonly carregandoMes: boolean;
  /** O dia aberto, `AAAA-MM-DD`, ou `null` (o mês inteiro à vista). */
  readonly dia: string | null;
  readonly aoAbrirDia: (dia: string | null) => void;
  readonly carregandoDia: boolean;
  /** Até onde as setas andam, `AAAA-MM`. */
  readonly primeiroMes: string;
  readonly ultimoMes: string;
};

export type CalendarioDeVagasProps = {
  /** As vagas; no `paginado`, só as do dia aberto. */
  vagas: readonly VagaDoCalendario[];
  /** Por mês: o mês e o dia vêm de fora, e os dias com vaga também. */
  paginado?: CalendarioPaginado | undefined;
  /** O id da vaga escolhida, ou `null`. */
  escolhida: string | null;
  aoEscolher: (id: string) => void;
  /** De quem são as vagas, para o leitor de tela: "Vagas livres de Dra. Ana". */
  rotulo: string;
  /** Enquanto houver motivo, nenhum horário troca (o envio em curso, por exemplo). */
  motivoTravado?: string;
  /** Hoje, `AAAA-MM-DD`, para o sublinhado. Padrão: o relógio de quem está na tela. */
  hoje?: string;
};

/** "terça, 28 de outubro". */
export function diaPorExtenso(dia: string): string {
  const p = partes(dia);
  return `${SEMANA[new Date(p.ano, p.mes, p.dia).getDay()]}, ${p.dia} de ${MESES[p.mes]}`;
}

/** Manhã até 12h, tarde até 18h, noite depois. */
function periodoDe(hora: string): 'Manhã' | 'Tarde' | 'Noite' {
  const h = Number(hora.slice(0, 2));
  return h < 12 ? 'Manhã' : h < 18 ? 'Tarde' : 'Noite';
}

const quantas = (n: number) => `${n} ${n === 1 ? 'vaga' : 'vagas'}`;

export function CalendarioDeVagas({
  vagas,
  paginado,
  escolhida,
  aoEscolher,
  rotulo,
  motivoTravado,
  hoje,
}: CalendarioDeVagasProps) {
  const id = useId();
  const hojeDia = hoje ?? hojeLocal();

  const porDia = new Map<string, VagaDoCalendario[]>();
  for (const vaga of vagas) porDia.set(vaga.dia, [...(porDia.get(vaga.dia) ?? []), vaga]);
  const dias = [...porDia.keys()].sort();
  const vagaEscolhida = vagas.find((vaga) => vaga.id === escolhida) ?? null;

  // Abre no dia da vaga escolhida, ou no primeiro dia com vaga.
  const [abertoLocal, definirAbertoLocal] = useState<string | null>(
    vagaEscolhida?.dia ?? dias[0] ?? null,
  );
  const inicio = partes(vagaEscolhida?.dia ?? dias[0] ?? hojeDia);
  const [mesLocal, definirMesLocal] = useState({ ano: inicio.ano, mes: inicio.mes });

  // No paginado, o mês e o dia aberto são de quem monta.
  const aberto = paginado ? paginado.dia : abertoLocal;
  const definirAberto = (dia: string | null) => {
    if (paginado) paginado.aoAbrirDia(dia);
    else definirAbertoLocal(dia);
  };
  const mes = paginado ? partes(`${paginado.mes}-01`) : mesLocal;
  /** Quantas vagas livres o dia tem: no paginado, a contagem do mês. */
  const livresNo = (dia: string): number =>
    paginado ? (paginado.diasComVaga[dia] ?? 0) : (porDia.get(dia)?.length ?? 0);

  /*
   * O dia aberto sumiu das vagas (recarregou e ele lotou): abre o primeiro dia
   * que ainda tem, ou volta ao calendário; no próprio render, sem efeito. No
   * paginado, quem monta decide.
   */
  if (!paginado && abertoLocal && !porDia.has(abertoLocal)) definirAbertoLocal(dias[0] ?? null);

  const primeiro = partes(paginado ? `${paginado.primeiroMes}-01` : (dias[0] ?? hojeDia));
  const ultimo = partes(paginado ? `${paginado.ultimoMes}-01` : (dias[dias.length - 1] ?? hojeDia));
  const indice = (p: { ano: number; mes: number }) => p.ano * 12 + p.mes;
  const temAntes = indice(mes) > indice(primeiro);
  const temDepois = indice(mes) < indice(ultimo);
  const andar = (delta: number) => {
    const d = new Date(mes.ano, mes.mes + delta, 1);
    if (paginado) paginado.aoMudarMes(diaDe(d.getFullYear(), d.getMonth(), 1).slice(0, 7));
    else definirMesLocal({ ano: d.getFullYear(), mes: d.getMonth() });
  };

  const vazias = new Date(mes.ano, mes.mes, 1).getDay();
  const diasNoMes = new Date(mes.ano, mes.mes + 1, 0).getDate();
  const casas = Array.from({ length: diasNoMes }, (_, i) => diaDe(mes.ano, mes.mes, i + 1));

  const doDia = aberto ? (porDia.get(aberto) ?? []) : [];
  const periodos = new Map<string, VagaDoCalendario[]>();
  for (const vaga of [...doDia].sort((a, b) => a.hora.localeCompare(b.hora))) {
    const periodo = periodoDe(vaga.hora);
    periodos.set(periodo, [...(periodos.get(periodo) ?? []), vaga]);
  }

  return (
    <div className="calendario-de-vagas" role="group" aria-label={rotulo}>
      <div className="calendario-de-vagas__quadro" data-aberto={aberto ? 'horarios' : 'dias'}>
        <div className="calendario-de-vagas__dias">
          <div className="calendario-de-vagas__cab">
            <button
              type="button"
              className="calendario-de-vagas__seta"
              onClick={() => andar(-1)}
              disabled={!temAntes}
              aria-label="Mês anterior"
              title={
                temAntes
                  ? undefined
                  : paginado
                    ? 'Este é o primeiro mês da agenda.'
                    : 'Não há vagas antes deste mês.'
              }
            >
              <Icone nome="voltar" />
            </button>
            <h6 className="calendario-de-vagas__mes" id={`${id}-mes`} aria-live="polite">
              {MESES[mes.mes]} {mes.ano}
            </h6>
            <button
              type="button"
              className="calendario-de-vagas__seta"
              onClick={() => andar(1)}
              disabled={!temDepois}
              aria-label="Próximo mês"
              title={
                temDepois
                  ? undefined
                  : paginado
                    ? 'A agenda não vai além deste mês.'
                    : 'Não há vagas depois deste mês.'
              }
            >
              <Icone nome="direita" />
            </button>
          </div>
          <div
            className="calendario-de-vagas__grade"
            role="group"
            aria-labelledby={`${id}-mes`}
            aria-busy={paginado?.carregandoMes || undefined}
          >
            {INICIAIS.map((inicial, i) => (
              <span key={i} className="calendario-de-vagas__semana" aria-hidden="true">
                {inicial}
              </span>
            ))}
            {Array.from({ length: vazias }, (_, i) => (
              <span key={`vazia-${i}`} aria-hidden="true" />
            ))}
            {casas.map((dia) => {
              const livres = livresNo(dia);
              return (
                <button
                  key={dia}
                  type="button"
                  className="calendario-de-vagas__dia"
                  data-com-vaga={livres > 0 || undefined}
                  data-hoje={dia === hojeDia || undefined}
                  aria-pressed={dia === aberto}
                  aria-label={`${diaPorExtenso(dia)}, ${livres > 0 ? quantas(livres) : 'sem vaga'}`}
                  disabled={livres === 0}
                  onClick={() => definirAberto(dia)}
                >
                  {partes(dia).dia}
                </button>
              );
            })}
          </div>
          <p className="calendario-de-vagas__legenda" aria-live="polite">
            <span className="calendario-de-vagas__amostra" aria-hidden="true" />
            {paginado?.carregandoMes ? 'Buscando os dias com vaga…' : 'Dia com vaga'}
          </p>
        </div>

        <div className="calendario-de-vagas__horarios">
          {aberto && (
            <>
              <div className="calendario-de-vagas__cab-horarios">
                <button
                  type="button"
                  className="calendario-de-vagas__seta"
                  onClick={() => definirAberto(null)}
                  aria-label="Voltar ao calendário"
                >
                  <Icone nome="voltar" />
                </button>
                <div>
                  <h6 className="calendario-de-vagas__dia-aberto" id={`${id}-dia`}>
                    {diaPorExtenso(aberto)}
                  </h6>
                  <p className="calendario-de-vagas__apoio">
                    {paginado?.carregandoDia
                      ? 'Buscando os horários…'
                      : `${quantas(doDia.length)} livres`}
                  </p>
                </div>
              </div>
              <div className="calendario-de-vagas__periodos">
                {[...periodos].map(([periodo, lista]) => (
                  <fieldset
                    key={periodo}
                    className="calendario-de-vagas__periodo"
                    title={motivoTravado}
                  >
                    <legend className="calendario-de-vagas__rotulo-periodo">{periodo}</legend>
                    <div className="calendario-de-vagas__lista">
                      {lista.map((vaga) => (
                        <label key={vaga.id} className="calendario-de-vagas__hora">
                          <input
                            type="radio"
                            name={`${id}-vaga`}
                            value={vaga.id}
                            checked={vaga.id === escolhida}
                            disabled={motivoTravado !== undefined}
                            onChange={() => aoEscolher(vaga.id)}
                          />
                          {vaga.hora}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
